import { CHANGELOG } from '../data/changelog';
import { useDocumentHead } from '../hooks/useDocumentHead';

/**
 * The full log, on its own page.
 *
 * It started life as a section on the landing page, which was the wrong place
 * for it twice over: it competed with the demo for the same attention, and it
 * gets longer every week, so a surface that has to stay short was going to
 * have to start truncating it. Here it can just grow.
 *
 * Grouped by date so a day with four entries reads as one day of work rather
 * than four separate events.
 */
function groupByDate(): { date: string; summaries: string[] }[] {
    const groups: { date: string; summaries: string[] }[] = [];

    for (const entry of CHANGELOG) {
        const last = groups.at(-1);
        if (last && last.date === entry.date) {
            last.summaries.push(entry.summary);
            continue;
        }
        groups.push({ date: entry.date, summaries: [entry.summary] });
    }

    return groups;
}

export function ChangelogPage() {
    useDocumentHead({
        title: 'Changelog: ActualSold',
        description:
            'What shipped and when. Parser fixes, new query parameters, billing and docs changes to the ActualSold eBay sold-price API.',
        canonicalPath: '/changelog',
    });

    const groups = groupByDate();

    return (
        <div className="pb-16">
            <header className="space-y-3">
                <p className="eyebrow text-text-muted">Changelog</p>
                <h1 className="text-title">What shipped, and when</h1>
                <p className="max-w-xl leading-relaxed text-text-muted">
                    Every change that affects the API surface, the rows it returns, or what you pay
                    for them. Parser work that nobody would notice is left out.
                </p>
            </header>

            <div className="mt-12 space-y-10">
                {groups.map((group) => (
                    <section key={group.date} className="border-t border-border pt-5 sm:flex sm:gap-10">
                        <h2 className="shrink-0 sm:w-32">
                            <time dateTime={group.date} className="font-mono text-xs text-text-muted">
                                {group.date}
                            </time>
                        </h2>
                        <ul className="mt-3 flex-1 space-y-3 sm:mt-0">
                            {group.summaries.map((summary) => (
                                <li key={summary} className="text-sm leading-relaxed text-text">
                                    {summary}
                                </li>
                            ))}
                        </ul>
                    </section>
                ))}
            </div>
        </div>
    );
}
